// @vendors
import {
    UserIcon,
    ClipboardDocumentCheckIcon,
    DocumentTextIcon,
    Cog6ToothIcon,
    SunIcon,
    ArrowLeftStartOnRectangleIcon,
    ChatBubbleLeftEllipsisIcon,
    CurrencyDollarIcon,
    MoonIcon
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

// @store
import useAuthStore from "../../../store/useAuthStore";
import { useThemeStore } from "../../../store/useThemeStore";
import { useCompanyStore } from "../../../store/useCompanyStore";
import { useLogoutStore } from "../../../store/useLogoutStore";

// @helpers
import routesConfig from "../../../config/routes.config";
import { getNameUser } from "../../../helpers/functionsUtils";

// @assets
import avatar from "../../../assets/icons/user-circle-avatar.svg";

export interface INavItemLink {
    href: string;
    icon: JSX.Element;
    label: string;
}

const SidebarProfileUser = () => {
    const { t } = useTranslation();
    const { roleList } = useAuthStore();
    const navigate = useNavigate();
    const { isDarkMode, toggleDarkMode } = useThemeStore();
    const { data, userData } = useCompanyStore();
    const { handleLogout } = useLogoutStore();

    const navItems = [
        {
            href: routesConfig.userProfile,
            icon: <UserIcon className="w-6 h-6" />,
            label: t("userProfile.aside.myAccount"),
        },
        {
            href: routesConfig.messageUser,
            icon: <ChatBubbleLeftEllipsisIcon className="w-6 h-6" />,
            label: t("userProfile.aside.message"),
        },
        {
            href: routesConfig.paymentUser,
            icon: <CurrencyDollarIcon className="w-6 h-6" />,
            label: t("userProfile.aside.payments"),
        },
        {
            href: routesConfig.contactUser,
            icon: <Cog6ToothIcon className="w-6 h-6" />,
            label: t("userProfile.aside.contact"),
        },
    ];

    const componentNav = (key: number, props: INavItemLink) => {
        return (
            <button
                key={key}
                onClick={() => { navigate(props.href) }}
                className={`flex items-center text-[#2C2C2C] transition gap-[10px] ${window.location.pathname === props.href ? 'bg-primary-50 text-[#F97068] dark:text-[#F97068]' : 'hover:border-l-2 border-[#F97068] text-gray-700 dark:text-white rounded-sm'}`}
            >
                {props.icon}
                <span className="font-medium whitespace-nowrap hidden lg:flex text-[14px]">
                    {props.label}
                </span>
            </button>
        )
    }

    return (
        <aside
            className={`h-[100vh] bg-white dark:bg-dark-cBackground text-cText dark:text-white rounded-lg shadow-lg dark:shadow-white transition-shadow duration-300 p-4 w-[50px] lg:w-64 ml-5 mt-2 `}
        >
            <div className="flex flex-col gap-2">
                <div className="flex flex-col justify-center items-center py-[20px] lg:p-[20px] gap-2">
                    <div className="flex justify-center">
                        <img
                            src={avatar}
                            alt="Logo"
                            className="w-[20px] lg:w-[80px] h-auto "
                        />
                    </div>
                    <h5 className="w-full hidden lg:flex items-center justify-center text-[#444A6D] dark:text-white text-[15px] font-medium">
                        {getNameUser(roleList, data, userData)}
                    </h5>
                </div>

                {/* Navigation */}
                <nav className="space-y-[30px]">
                    {
                        navItems.map((item, index) => (
                            componentNav(index, item)
                        ))
                    }

                    <div className="w-full flex gap-2">
                        <Link
                            target="_blank"
                            to={{ pathname: routesConfig.termsAndCondition }}
                            className="text-[#2C2C2C] dark:text-white flex items-center gap-2 hover:border-l-2 border-[#F97068]"
                        >
                            <ClipboardDocumentCheckIcon className="w-6 h-6" />
                            <span className="font-medium whitespace-nowrap hidden lg:flex text-[#2C2C2C] dark:text-white text-[14px]">
                                {t("userProfile.aside.terms")}
                            </span>
                        </Link>
                    </div>

                    <div className="w-full flex gap-2">
                        <Link
                            target="_blank"
                            to={{ pathname: routesConfig.dataProcessing }}
                            className="text-[#2C2C2C] dark:text-white flex items-center gap-2 hover:border-l-2 border-[#F97068]"
                        >
                            <DocumentTextIcon className="w-6 h-6" />
                            <span className="font-medium whitespace-nowrap hidden lg:flex text-[#2C2C2C] dark:text-white text-[14px]">
                                {t("userProfile.aside.dataProcessing")}
                            </span>
                        </Link>
                    </div>

                    <div className="w-full flex items-center hover:border-l-2 border-[#F97068]">
                        <button
                            onClick={() => handleLogout(true)}
                            className="text-[#2C2C2C] dark:text-white flex items-center gap-2"
                        >
                            <ArrowLeftStartOnRectangleIcon className="size-6" />
                            <span className="font-medium whitespace-nowrap hidden lg:flex text-[#2C2C2C] dark:text-white text-[14px]">
                                {t("userProfile.aside.exit")}
                            </span>
                        </button>
                    </div>

                    <div className="w-full flex hover:border-l-2 border-[#F97068]">
                        <button
                            onClick={toggleDarkMode}
                            className="text-[#2C2C2C] dark:text-white flex items-center gap-2"
                        >
                            {isDarkMode ? <SunIcon className="size-6" /> : <MoonIcon className="size-6" />}
                            <span className="font-medium whitespace-nowrap hidden lg:flex text-[#2C2C2C] dark:text-white text-[14px]">
                                {!isDarkMode ? t("professional.drawer.darkMode") : t("professional.drawer.lightMode")}
                            </span>
                        </button>
                    </div>
                </nav>
            </div>
        </aside>
    )
}

export default SidebarProfileUser